// src/services/auth.js
// Federate OIDC Authentication — Authorization Code Flow with PKCE
// With proactive silent refresh to prevent session timeouts

const FEDERATE_AUTHORIZE = 'https://idp.federate.amazon.com/api/oauth2/v1/authorize';
// Federate client id + API base are build-time configurable (beta vs prod).
// Defaults are the prod values so a plain `npm run build` produces the prod bundle unchanged.
const CLIENT_ID = process.env.REACT_APP_FEDERATE_CLIENT_ID || 'pam-permissions-auth';
const REDIRECT_URI = window.location.origin + '/';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://b70682k0f3.execute-api.eu-north-1.amazonaws.com/prod';

const SESSION_KEY = 'pam_auth_session';
const ATTEMPTED_KEY = 'auth_attempted';
const RETURN_URL_KEY = 'pam_auth_return_url';
const FORM_STATE_KEY = 'pam_form_state';

// Token lifetime: Federate issues ~1 hour tokens
// Refresh at 45 min to avoid expiry mid-request
const TOKEN_REFRESH_INTERVAL = 45 * 60 * 1000; // 45 minutes
const TOKEN_WARNING_BEFORE = 5 * 60 * 1000; // Show warning 5 min before expiry
const TOKEN_LIFETIME = 60 * 60 * 1000; // 1 hour (assumed)

let refreshTimer = null;
let warningTimer = null;
let onSessionWarning = null; // callback for UI warning

// ════════════════════════════════════════════
// Session management
// ════════════════════════════════════════════
const getSession = () => {
    try {
        const saved = sessionStorage.getItem(SESSION_KEY);
        if (!saved) return null;
        const session = JSON.parse(saved);
        if (session.expiresAt && Date.now() > session.expiresAt) {
            // Access token expired. Do NOT wipe storage — the refresh token outlives the
            // access token and is needed for silent renewal. Just report "no valid session".
            return null;
        }
        return session;
    } catch {
        return null;
    }
};

// Decode a JWT's payload without verifying (frontend only needs the exp claim to know
// when to refresh; the API Gateway authorizer does the real signature verification).
const decodeJwtPayload = (token) => {
    try {
        const parts = (token || '').split('.');
        if (parts.length !== 3) return null;
        let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        b64 = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
        return JSON.parse(atob(b64));
    } catch {
        return null;
    }
};

const setSession = (alias, accessToken, refreshToken) => {
    const now = Date.now();
    // Use the token's REAL expiry (exp claim) rather than a fixed 60-min assumption.
    // This is what the authorizer enforces, so the frontend must agree — otherwise it
    // keeps using an already-expired token and every API call 401s (blank UI).
    const claims = decodeJwtPayload(accessToken);
    const tokenExpMs = (claims && typeof claims.exp === 'number') ? claims.exp * 1000 : null;
    const expiresAt = tokenExpMs || (now + TOKEN_LIFETIME);
    // Preserve an existing refresh token if this call didn't supply a new one.
    const prev = (() => { try { return JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null'); } catch { return null; } })();
    const rt = refreshToken || (prev && prev.refreshToken) || '';
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({
        alias,
        accessToken: accessToken || '',
        refreshToken: rt,
        expiresAt,
        authenticatedAt: new Date().toISOString(),
        lastRefreshed: now
    }));
    // Start proactive refresh timer, scheduled from the real expiry.
    startRefreshTimer();
};

// Read the refresh token from raw storage — it MUST be readable even after the access
// token's expiry cleared the "valid session" view, since that's precisely when we renew.
const getRefreshToken = () => {
    try {
        const raw = sessionStorage.getItem(SESSION_KEY);
        if (!raw) return '';
        return JSON.parse(raw).refreshToken || '';
    } catch {
        return '';
    }
};

export const clearSession = () => {
    sessionStorage.removeItem(SESSION_KEY);
    stopRefreshTimer();
};

// Get the stored access token for API calls
export const getAccessToken = () => {
    const session = getSession();
    return session?.accessToken || '';
};

// Check if token is close to expiry
export const isTokenExpiringSoon = () => {
    const session = getSession();
    if (!session || !session.expiresAt) return true;
    return (session.expiresAt - Date.now()) < TOKEN_WARNING_BEFORE;
};

// Register a callback for session warning (used by UI components)
export const onSessionExpiryWarning = (callback) => {
    onSessionWarning = callback;
};

// ════════════════════════════════════════════
// Proactive silent refresh
// ════════════════════════════════════════════
const startRefreshTimer = () => {
    stopRefreshTimer();

    // Schedule the proactive refresh from the token's REAL expiry, with a safety buffer,
    // instead of a fixed 45-min timer that can outlive a short-lived token. Refresh at
    // (expiry - 5 min); clamp to a sane range so we never fire immediately or too late.
    const session = getSession();
    let delay = TOKEN_REFRESH_INTERVAL; // fallback
    if (session && session.expiresAt) {
        delay = session.expiresAt - Date.now() - TOKEN_WARNING_BEFORE;
        // Clamp: at least 10s out, at most the fixed interval.
        delay = Math.max(10 * 1000, Math.min(delay, TOKEN_REFRESH_INTERVAL));
    }

    warningTimer = setTimeout(async () => {
        // Prefer SILENT refresh (no redirect). Only fall back to a full Federate redirect
        // if silent refresh isn't possible (no/expired refresh token).
        const newToken = await silentRefresh();
        if (newToken) {
            console.log('[Auth] Proactive silent refresh succeeded — session extended.');
            return; // setSession already restarted the timer
        }
        if (onSessionWarning) onSessionWarning('Session refreshing...');
        console.log('[Auth] Silent refresh unavailable — redirecting to Federate...');
        if (window.__pamSaveFormState) window.__pamSaveFormState();
        sessionStorage.removeItem(SESSION_KEY);
        sessionStorage.setItem(RETURN_URL_KEY, window.location.pathname + window.location.search);
        redirectToFederate();
    }, delay);
};

const stopRefreshTimer = () => {
    if (refreshTimer) { clearTimeout(refreshTimer); refreshTimer = null; }
    if (warningTimer) { clearTimeout(warningTimer); warningTimer = null; }
};

// Silent refresh — renew the short-lived access token via the backend refreshToken op,
// using the stored refresh_token. No redirect, no user interruption. Returns the new
// access token on success, or null if refresh isn't possible (caller falls back to redirect).
// Single-flight: concurrent callers await the same in-flight refresh.
let _refreshInFlight = null;

const silentRefresh = async () => {
    const rt = getRefreshToken();
    if (!rt) return null; // no refresh token → can't renew silently

    if (_refreshInFlight) return _refreshInFlight;

    _refreshInFlight = (async () => {
        try {
            const resp = await fetch(`${API_BASE_URL}/auth`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    operation: 'refreshToken',
                    refreshToken: rt,
                    clientId: CLIENT_ID,
                })
            });
            const data = await resp.json();
            const result = typeof data.body === 'string' ? JSON.parse(data.body) : data;
            if (result && result.success && result.accessToken) {
                const alias = result.alias || getStoredAlias();
                setSession(alias, result.accessToken, result.refreshToken || rt);
                if (alias) localStorage.setItem('ufs_user_alias', String(alias).toLowerCase());
                console.log('[Auth] Silent refresh OK');
                return result.accessToken;
            }
            console.warn('[Auth] Silent refresh returned no token (reauth needed)');
            return null;
        } catch (e) {
            console.warn('[Auth] Silent refresh error:', e.message);
            return null;
        } finally {
            _refreshInFlight = null;
        }
    })();

    return _refreshInFlight;
};

const getStoredAlias = () => {
    try {
        const raw = sessionStorage.getItem(SESSION_KEY);
        if (raw) { const a = JSON.parse(raw).alias; if (a) return a; }
    } catch { /* ignore */ }
    return localStorage.getItem('ufs_user_alias') || '';
};

// Ensure a usable (non-expired) access token before an API call. If the current token is
// expired or about to expire, silently refresh first. Returns a valid token or '' if none.
export const ensureFreshToken = async () => {
    const session = getSession();
    if (session && session.accessToken && session.expiresAt && (session.expiresAt - Date.now()) > 60 * 1000) {
        return session.accessToken; // still comfortably valid
    }
    // Expired or within 60s of expiry → try silent refresh.
    const refreshed = await silentRefresh();
    return refreshed || '';
};

// ════════════════════════════════════════════
// Form state persistence (only used before forced redirect)
// ════════════════════════════════════════════
export const saveFormState = (formData) => {
    try {
        localStorage.setItem(FORM_STATE_KEY, JSON.stringify({
            data: formData,
            savedAt: Date.now(),
            path: window.location.pathname
        }));
    } catch (e) {
        console.warn('[Auth] Could not save form state:', e);
    }
};

export const loadFormState = () => {
    try {
        const saved = localStorage.getItem(FORM_STATE_KEY);
        if (!saved) return null;
        const parsed = JSON.parse(saved);
        // Only restore if saved less than 10 minutes ago
        if (Date.now() - parsed.savedAt > 10 * 60 * 1000) {
            localStorage.removeItem(FORM_STATE_KEY);
            return null;
        }
        return parsed.data;
    } catch {
        return null;
    }
};

export const clearFormState = () => {
    localStorage.removeItem(FORM_STATE_KEY);
};

// ════════════════════════════════════════════
// Retry auth on 401 (called from api.js)
// Tries silent refresh first, only redirects as last resort
// ════════════════════════════════════════════
export const handleAuthFailure = async (formDataToSave = null) => {
    console.log('[Auth] Handling auth failure — attempting silent refresh first...');

    // ── Try silent refresh FIRST ─────────────────────────────────────────
    // A 401 on a short-lived token is usually just expiry. If we hold a valid refresh
    // token, renew silently and signal the caller to retry — no redirect, no lost work.
    const refreshed = await silentRefresh();
    if (refreshed) {
        console.log('[Auth] Silent refresh recovered the session — caller should retry.');
        return true; // truthy → api layer retries the original request
    }

    // ── Redirect-loop guard ──────────────────────────────────────────────
    // If we already re-authenticated very recently and STILL got a 401, another
    // redirect would loop forever (e.g. a token that logs in but can't validate).
    // In that case, stop redirecting and let the caller surface a message instead.
    const LOOP_KEY = 'pam_reauth_at';
    const lastReauth = parseInt(sessionStorage.getItem(LOOP_KEY) || '0', 10);
    if (lastReauth && (Date.now() - lastReauth) < 90 * 1000) {
        console.warn('[Auth] Re-auth already attempted <90s ago and still failing — NOT redirecting (loop guard).');
        if (onSessionWarning) onSessionWarning('Session could not be validated. Please refresh the page or sign in again.');
        return false;
    }
    sessionStorage.setItem(LOOP_KEY, String(Date.now()));

    // Save form state before redirect (so user doesn't lose work)
    // Try the global save function first (saves current React state directly)
    if (window.__pamSaveFormState) {
        window.__pamSaveFormState();
    } else if (formDataToSave) {
        saveFormState(formDataToSave);
    }
    
    // Save current URL
    sessionStorage.setItem(RETURN_URL_KEY, window.location.pathname + window.location.search);
    
    // Clear expired session
    sessionStorage.removeItem(SESSION_KEY);
    
    // Redirect to Federate — if Midway is active, this is instant (< 2 seconds)
    redirectToFederate();
    
    // Return false — page is redirecting, caller should not retry
    return false;
};

// ════════════════════════════════════════════
// Get current user alias
// ════════════════════════════════════════════
export const getCurrentUser = async () => {
    // Skip auth on localhost
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        return localStorage.getItem('ufs_user_alias') || null;
    }

    // 1. Check existing session
    const session = getSession();
    if (session && session.alias) {
        startRefreshTimer(); // Ensure timer is running
        return session.alias;
    }

    // 2. Check if returning from Federate with auth code
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');

    if (code) {
        // Clean URL immediately
        const returnUrl = sessionStorage.getItem(RETURN_URL_KEY) || window.location.pathname;
        sessionStorage.removeItem(RETURN_URL_KEY);
        window.history.replaceState({}, '', returnUrl);

        try {
            console.log('[Auth] Exchanging code...');
            const codeVerifier = sessionStorage.getItem('pkce_code_verifier') || '';
            sessionStorage.removeItem('pkce_code_verifier');
            const authResult = await exchangeCodeForAlias(code, codeVerifier);
            console.log('[Auth] Got alias:', authResult?.alias);
            if (authResult && authResult.alias) {
                setSession(authResult.alias, authResult.accessToken, authResult.refreshToken);
                localStorage.setItem('ufs_user_alias', authResult.alias.toLowerCase());
                sessionStorage.removeItem(ATTEMPTED_KEY);
                return authResult.alias;
            }
        } catch (err) {
            console.error('[Auth] Exchange failed:', err.message);
        }

        // Exchange failed — don't loop
        return localStorage.getItem('ufs_user_alias') || null;
    }

    // 3. Redirect to Federate (only once per page load to prevent loops)
    if (sessionStorage.getItem(ATTEMPTED_KEY)) {
        sessionStorage.removeItem(ATTEMPTED_KEY);
        return localStorage.getItem('ufs_user_alias') || null;
    }

    sessionStorage.setItem(ATTEMPTED_KEY, 'true');
    if (!sessionStorage.getItem(RETURN_URL_KEY)) {
        sessionStorage.setItem(RETURN_URL_KEY, window.location.pathname + window.location.search);
    }
    redirectToFederate();
    return null;
};

// ════════════════════════════════════════════
// Redirect to Federate
// ════════════════════════════════════════════
const redirectToFederate = () => {
    const codeVerifier = generateCodeVerifier();
    sessionStorage.setItem('pkce_code_verifier', codeVerifier);

    generateCodeChallenge(codeVerifier).then(codeChallenge => {
        const params = new URLSearchParams({
            client_id: CLIENT_ID,
            response_type: 'code',
            redirect_uri: REDIRECT_URI,
            // Request offline_access so Federate returns a refresh_token, enabling silent
            // renewal of the short-lived (~15 min) access token without a full redirect.
            scope: 'openid offline_access',
            code_challenge: codeChallenge,
            code_challenge_method: 'S256'
        });
        window.location.replace(`${FEDERATE_AUTHORIZE}?${params.toString()}`);
    });
};

// ════════════════════════════════════════════
// Exchange code for alias via Lambda
// ════════════════════════════════════════════
const exchangeCodeForAlias = async (code, codeVerifier) => {
    const response = await fetch(`${API_BASE_URL}/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            operation: 'exchangeAuthCode',
            code,
            redirectUri: REDIRECT_URI,
            codeVerifier: codeVerifier || '',
            clientId: CLIENT_ID
        })
    });

    if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(`Auth exchange failed: ${response.status} ${text}`);
    }

    const data = await response.json();
    const result = typeof data.body === 'string' ? JSON.parse(data.body) : data;
    return {
        alias: result.alias || null,
        accessToken: result.accessToken || '',
        refreshToken: result.refreshToken || '',
    };
};

// ════════════════════════════════════════════
// PKCE helpers
// ════════════════════════════════════════════
const generateCodeVerifier = () => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return base64UrlEncode(array);
};

const generateCodeChallenge = async (verifier) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return base64UrlEncode(new Uint8Array(digest));
};

const base64UrlEncode = (buffer) => {
    return btoa(String.fromCharCode(...buffer))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
};
